import { useState } from 'react';

import StatusCell from '../../../components/Badge/StatusCell';
import { PrimaryBlackButton, PrimaryWhiteButton } from '../../../components/Button/Buttons';
import {
  ActionRow,
  EventRow,
  Heading,
  LinkButton,
  LoadingText,
  Muted,
  Row,
  Section,
  SectionTitle,
} from '../../../components/Detail/DetailParts';
import TableSliderContentWrapper from '../../../components/Table/TableAccessories/TableSliderContentWrapper';
import useScheduleDetail, { ScheduleAction } from '../../../hooks/dashboard/schedules/useScheduleDetail';
import {
  PAUSE_REASON_LABELS,
  SCHEDULE_KIND_LABELS,
  ScheduleRow,
} from '../../../models/schedules/schedule.model';
import { capitalize, formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';
import { TransactionDetailBody } from '../Transactions/TransactionDetailSlider';
import DisputeRecord from './DisputeRecord';

const when = (value?: string | null) => (value ? tableFormatDateTimeNG(value) : '—');
const words = (value?: string | null) => (value ? capitalize(value.replace(/_/g, ' ')) : '—');

// The backend refuses run-now in production; hide it in the production build too.
const CAN_RUN_NOW = process.env.REACT_APP_ENV !== 'PROD';

const ScheduleDetailSlider: React.FC<{ data: ScheduleRow }> = ({ data }) => {
  const { schedule, loading, saving, act } = useScheduleDetail(data.id);
  const [selected, setSelected] = useState<string | null>(null);
  const current = schedule ?? data;
  const open = current.status === 'ACTIVE' || current.status === 'PAUSED';

  const run = (action: ScheduleAction, question: string) => {
    if (window.confirm(question)) act(action);
  };

  return (
    <TableSliderContentWrapper label='Scheduled payment'>
      <Section>
        <Heading>
          {current.code} · {current.name}
        </Heading>
        <StatusCell data={current} statusKey='status' hasDot />
        {current.pause_reason && (
          <Row label='Paused because'>{PAUSE_REASON_LABELS[current.pause_reason] ?? words(current.pause_reason)}</Row>
        )}
        <Row label='Customer'>{current.chat_id ?? '—'}</Row>
        <Row label='Bill'>
          {words(current.bill_type)} · {current.service_id}
        </Row>
        <Row label='Recipient'>{current.identifier}</Row>
        <Row label='Amount'>{formatNaira(Number(current.amount))}</Row>
        <Row label='Cap per payment'>{formatNaira(Number(current.max_amount))} plus any fee</Row>
        <Row label='Repeats'>{SCHEDULE_KIND_LABELS[current.kind] ?? words(current.kind)}</Row>
        {schedule && <Row label='Time'>{schedule.run_time} Lagos</Row>}
        <Row label='Next payment'>{current.next_run_date ?? '—'}</Row>
        <Row label='Declines in a row'>{current.failures_in_a_row}</Row>
        {schedule && <Row label='Confirmed with PIN'>{when(schedule.consented_at)}</Row>}
      </Section>

      {open && (
        <Section>
          <SectionTitle>Actions</SectionTitle>
          <ActionRow>
            {current.status === 'ACTIVE' && (
              <PrimaryWhiteButton
                type='button'
                disabled={saving}
                onClick={() => run('pause', 'Pause this schedule? Only the user can resume it, with their PIN.')}
              >
                Pause
              </PrimaryWhiteButton>
            )}
            <PrimaryBlackButton
              type='button'
              disabled={saving}
              onClick={() => run('cancel', 'Cancel this schedule for good? The user is told.')}
            >
              Cancel schedule
            </PrimaryBlackButton>
            {CAN_RUN_NOW && current.status === 'ACTIVE' && (
              <PrimaryWhiteButton
                type='button'
                disabled={saving}
                onClick={() => run('run-now', 'Create and charge today’s run now? Staging only.')}
              >
                Run now (staging)
              </PrimaryWhiteButton>
            )}
          </ActionRow>
          <Muted>There is no resume here: a paused schedule restarts only when the user confirms it with their PIN.</Muted>
        </Section>
      )}

      {loading && !schedule && <LoadingText>Loading…</LoadingText>}

      {schedule && (
        <Section>
          <SectionTitle>Runs</SectionTitle>
          {schedule.runs.length === 0 && <Muted>No runs yet.</Muted>}
          {schedule.runs.map((r) => {
            const reference = r.transaction_reference ?? r.pay_now_reference;
            return (
              <EventRow key={r.id}>
                <span>
                  {r.due_date}
                  <br />
                  <StatusCell data={r} statusKey='status' />
                </span>
                <span>
                  {r.amount ? formatNaira(Number(r.amount)) : '—'}
                  {r.skip_reason && <Muted>Skipped: {words(r.skip_reason)}</Muted>}
                  {r.failure_kind && <Muted>{words(r.failure_kind)}</Muted>}
                  {r.pay_now_reference && <Muted>Paid with Pay now</Muted>}
                </span>
                <span>
                  {reference ? (
                    <LinkButton type='button' onClick={() => setSelected(reference)}>
                      {selected === reference ? 'Shown below' : 'Charge & dispute record'}
                    </LinkButton>
                  ) : (
                    '—'
                  )}
                </span>
              </EventRow>
            );
          })}
        </Section>
      )}

      {selected && (
        <>
          <DisputeRecord reference={selected} />
          <SectionTitle>Transaction</SectionTitle>
          <TransactionDetailBody reference={selected} />
        </>
      )}

      {schedule && schedule.audits.length > 0 && (
        <Section>
          <SectionTitle>History</SectionTitle>
          {schedule.audits.map((audit, index) => (
            <EventRow key={index}>
              <span>
                {words(audit.event_type)}
                <br />
                <Muted>{audit.actor ?? 'system'}</Muted>
              </span>
              <span>
                {audit.previous_status || audit.new_status
                  ? `${words(audit.previous_status)} → ${words(audit.new_status)}`
                  : ''}
              </span>
              <span>{when(audit.created_at)}</span>
            </EventRow>
          ))}
        </Section>
      )}
    </TableSliderContentWrapper>
  );
};

export default ScheduleDetailSlider;
